import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowRight, Star, BookOpen, Flame, Zap } from 'lucide-react'
import { categories, allAlgorithms } from '../data/algorithms'
import { useHeroEntrance, useScrollReveal } from '../hooks/useScrollAnimation'

const stats = [
  { value: '50+', label: 'Algorithms Implemented' },
  { value: '12K+', label: 'GitHub Stars' },
  { value: '2.5K+', label: 'Forks' },
  { value: '500+', label: 'Contributors' },
]

const featuredAlgos = allAlgorithms.slice(0, 4)

export default function Home() {
  const heroRef = useHeroEntrance()
  const scrollRef = useScrollReveal()

  return (
    <div ref={scrollRef}>
      {/* Hero Section — Asymmetric, Editorial, Parallax */}
      <section ref={heroRef} className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden">
        {/* Background parallax layers */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 right-0 w-[600px] h-[600px] rounded-full bg-copper/3 blur-3xl animate-breathe" />
          <div className="absolute bottom-1/4 left-0 w-[400px] h-[400px] rounded-full bg-teal/3 blur-3xl animate-breathe animation-delay-400" />
        </div>

        <div className="section-base relative z-10">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Content — Asymmetric, takes 7 columns */}
            <div className="lg:col-span-7">
              <div className="hero-label inline-block mb-8">
                <span className="eyebrow">Algorithmic Engineering</span>
              </div>

              <h1 className="headline-xl mb-8">
                <span className="hero-title-line block text-cream">Research</span>
                <span className="hero-title-line block text-copper">Decoded</span>
                <span className="hero-title-line block text-cream">& Deployed</span>
              </h1>

              <p className="hero-description body-large max-w-xl mb-10">
                We take the world's most impactful algorithms — from research papers, social media platforms, and our own lab — and turn them into production-ready code with full documentation.
              </p>

              <div className="hero-actions flex flex-wrap gap-4 mb-16">
                <Link to="/archive" className="btn-copper group">
                  Explore The Archive
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <a
                  href="https://github.com/quantalgolabs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ghost"
                >
                  <Star size={15} />
                  View on GitHub
                </a>
              </div>

              <div className="flex flex-wrap gap-10">
                {stats.map((stat, i) => (
                  <div key={i} className="hero-metric">
                    <div className="font-display text-2xl sm:text-3xl font-bold text-copper tracking-tight">
                      {stat.value}
                    </div>
                    <div className="text-xs font-mono uppercase tracking-wider text-cream-dark mt-1">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Visual — Code block, 5 columns */}
            <div className="lg:col-span-5 hidden lg:block">
              <div className="relative">
                <div className="bg-ink-card border border-ink-border rounded-lg p-6 font-mono text-sm leading-relaxed shadow-2xl shadow-black/40">
                  <div className="flex gap-2 mb-5">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
                  </div>
                  <div className="space-y-1">
                    <div><span className="text-copper">class</span> <span className="text-cream">QuantAlgoLab</span>:</div>
                    <div className="pl-4"><span className="text-cream-dark"># Paper → Code → Production</span></div>
                    <div className="pl-4"><span className="text-teal-light">def</span> <span className="text-cream">from_paper</span>(<span className="text-cream-muted">paper</span>):</div>
                    <div className="pl-8"><span className="text-cream-dark"># Read, understand, implement</span></div>
                    <div className="pl-8"><span className="text-teal">return</span> <span className="text-copper">ProductionCode</span>(paper)</div>
                    <div className="pl-4"><span className="text-teal-light">def</span> <span className="text-cream">from_platform</span>(<span className="text-cream-muted">platform</span>):</div>
                    <div className="pl-8"><span className="text-cream-dark"># Reverse-engineer, model, deploy</span></div>
                    <div className="pl-8"><span className="text-teal">return</span> <span className="text-copper">ReconstructedEngine</span>(platform)</div>
                    <div className="pl-4"><span className="text-teal-light">def</span> <span className="text-cream">from_scratch</span>():</div>
                    <div className="pl-8"><span className="text-cream-dark"># Innovate, test, iterate</span></div>
                    <div className="pl-8"><span className="text-teal">return</span> <span className="text-copper">OriginalAlgorithm</span>()</div>
                  </div>
                </div>

                {/* Floating metric card */}
                <motion.div
                  className="absolute -top-6 -right-4 bg-ink-card border border-ink-border rounded-lg p-4 shadow-xl"
                  animate={{ y: [0, -12, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <div className="font-mono text-xs text-cream-dark mb-1">SHARPE RATIO</div>
                  <div className="font-display text-xl font-bold text-copper">2.34</div>
                </motion.div>

                <motion.div
                  className="absolute -bottom-4 -left-4 bg-ink-card border border-ink-border rounded-lg p-4 shadow-xl"
                  animate={{ y: [0, 12, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
                >
                  <div className="font-mono text-xs text-cream-dark mb-1">BLEU SCORE</div>
                  <div className="font-display text-xl font-bold text-teal-light">27.3</div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee Strip */}
      <section className="py-6 border-y border-ink-border overflow-hidden bg-ink-card/50">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...Array(2)].map((_, setIdx) => (
            <div key={setIdx} className="flex items-center gap-12 px-6">
              {['Transformers', 'AlphaZero', 'DQN', 'BERT', 'GCN', 'TikTok FYP', 'Twitter Ranking', 'LinkedIn Feed', 'Momentum Trading', 'Sentiment Engine', 'Portfolio Optimizer'].map((name) => (
                <span key={`${setIdx}-${name}`} className="flex items-center gap-3 text-sm font-mono text-cream-dark uppercase tracking-wider">
                  <span className="w-1.5 h-1.5 rounded-full bg-copper/60" />
                  {name}
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>

      {/* Three Labs — Flat Surfaces, No Card Nesting */}
      <section className="section-pad">
        <div className="section-base">
          <div className="reveal-up mb-20">
            <span className="eyebrow block mb-4">Three Labs</span>
            <h2 className="headline-lg text-cream">
              Where <span className="text-copper">Algorithms</span> Are Born
            </h2>
            <p className="body-large max-w-2xl mt-6">
              Every algorithm is documented, explained, and connected to its source — whether it came from a research paper, a social media platform, or our own innovation lab.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-ink-border rounded-lg overflow-hidden">
            {categories.map((cat, i) => (
              <motion.div
                key={cat.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.8, delay: i * 0.15, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="bg-ink-card p-8 lg:p-10 group"
              >
                <div className="flex items-center justify-between mb-8">
                  <span className="font-mono text-xs text-cream-dark uppercase tracking-wider">0{i + 1}</span>
                  <span className="text-2xl">{cat.icon}</span>
                </div>

                <h3 className="headline-sm text-cream mb-2 group-hover:text-copper transition-colors duration-300">
                  {cat.title}
                </h3>
                <p className="text-xs font-mono uppercase tracking-wider text-copper mb-6">
                  {cat.subtitle}
                </p>

                <p className="body-text mb-8">
                  {cat.description}
                </p>

                <div className="space-y-2 mb-8">
                  {cat.features.map((f) => (
                    <div key={f} className="flex items-center gap-2 text-sm text-cream-muted">
                      <span className="w-1 h-1 rounded-full bg-copper/50" />
                      {f}
                    </div>
                  ))}
                </div>

                <Link
                  to={cat.path}
                  className="inline-flex items-center gap-2 text-sm font-medium text-cream hover:text-copper transition-colors duration-300 group/link"
                >
                  {cat.count} Algorithms
                  <ArrowRight size={14} className="group-hover/link:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured — Editorial List, Not Grid */}
      <section className="section-pad bg-ink-card/40">
        <div className="section-base">
          <div className="reveal-up mb-16">
            <span className="eyebrow block mb-4">Recently Implemented</span>
            <h2 className="headline-lg text-cream">
              Fresh from the <span className="text-teal-light">Lab</span>
            </h2>
          </div>

          <div className="divide-y divide-ink-border">
            {featuredAlgos.map((algo, i) => (
              <motion.div
                key={algo.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
              >
                <Link
                  to={`/algorithm/${algo.id}`}
                  className="group block py-8 lg:py-10 transition-colors duration-300"
                >
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 lg:gap-8">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        {algo.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="text-xs font-mono text-cream-dark uppercase tracking-wider">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h3 className="headline-sm text-cream group-hover:text-copper transition-colors duration-300 mb-2">
                        {algo.title}
                      </h3>
                      <p className="body-text max-w-2xl">
                        {algo.abstract}
                      </p>
                    </div>
                    <div className="flex items-center gap-6 text-sm text-cream-dark shrink-0 lg:pt-1">
                      <span className="flex items-center gap-1.5">
                        <Star size={14} className="text-copper" />
                        {algo.stars.toLocaleString()}
                      </span>
                      <span className="font-mono text-xs">{algo.language}</span>
                      <span className={`text-xs font-mono px-2 py-0.5 rounded ${
                        algo.difficulty === 'Expert' ? 'text-red-400/70' :
                        algo.difficulty === 'Advanced' ? 'text-yellow-400/70' :
                        'text-green-400/70'
                      }`}>
                        {algo.difficulty}
                      </span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="mt-12">
            <Link to="/archive" className="btn-ghost inline-flex">
              View All Algorithms
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Process — Editorial, Asymmetric */}
      <section className="section-pad">
        <div className="section-base">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-4 reveal-left">
              <span className="eyebrow block mb-4">Our Process</span>
              <h2 className="headline-lg text-cream sticky top-28">
                From Paper to <span className="text-copper">Production</span>
              </h2>
              <p className="body-text mt-6">
                Our structured process ensures every algorithm is properly understood, implemented, and deployed.
              </p>
            </div>

            <div className="lg:col-span-8 space-y-0">
              {[
                { num: '01', title: 'Research & Understand', desc: 'Deep-dive into the paper or algorithm. Understand every equation, assumption, and edge case before writing a single line of code.' },
                { num: '02', title: 'Build & Implement', desc: 'Write clean, modular code with comprehensive tests, type annotations, and thorough documentation. No shortcuts.' },
                { num: '03', title: 'Document & Explain', desc: 'Create step-by-step guides with visualizations, mathematical breakdowns, and real-world examples.' },
                { num: '04', title: 'Deploy & Demo', desc: 'Build interactive frontends, backend APIs, and deploy live demonstrations that anyone can try.' },
              ].map((step, i) => (
                <motion.div
                  key={step.num}
                  initial={{ opacity: 0, x: 40 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className="surface-item"
                >
                  <div className="flex gap-6 lg:gap-10">
                    <span className="font-mono text-xs text-copper uppercase tracking-wider shrink-0 w-8">
                      {step.num}
                    </span>
                    <div>
                      <h4 className="font-display font-semibold text-lg text-cream mb-2">{step.title}</h4>
                      <p className="body-text max-w-lg">{step.desc}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA — Minimal, Powerful */}
      <section className="section-pad bg-ink-card/40">
        <div className="section-base">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="eyebrow block mb-6">Ready to Explore?</span>
            <h2 className="headline-lg text-cream mb-8">
              Dive into the <span className="text-copper">Archive</span>, <span className="text-teal-light">Decode</span> a platform, or forge something <span className="text-copper">new</span>.
            </h2>
            <div className="flex flex-wrap gap-4">
              <Link to="/archive" className="btn-copper group">
                <BookOpen size={16} />
                The Archive
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/decode" className="btn-teal group">
                <Flame size={16} />
                The Decode
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link to="/forge" className="btn-ghost group">
                <Zap size={16} />
                The Forge
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
